import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import ApiBaseURL from '../ApiBaseUrl/ApiBaseURL';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DashboardVehicleRefilling = ({
    vehicleTypeId = null,
    vendorId = null,
    driverId = null,
    vehicleId = null,
    // period = '3m'
    period = null
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedRange, setSelectedRange] = useState(period);
    const [fuelData, setFuelData] = useState({});

    const navigate = useNavigate();

    const monthNames = [
        '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    useEffect(() => {
        fetchFuelSummary();
    }, [vehicleTypeId, vendorId, driverId, vehicleId, selectedRange, period]);

    const fetchFuelSummary = async () => {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');

        if (!userId || !token) {
            toast.error('Missing userId or token in localStorage');
            return;
        }

        try {
            const resp = await fetch(`${ApiBaseURL}/dashboard/v1/get/monthly-fuel-summary`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    userId,
                    Authorization: `Bearer ${token}`
                },
                // body: JSON.stringify({
                //     vehicleTypeId,
                //     vendorId,
                //     driverId,
                //     vehicleId,
                //     // period: selectedRange.toLowerCase()
                //     period
                // })
                body: JSON.stringify({
                    vehicleTypeId,
                    vendorId,
                    driverId,
                    vehicleId,
                    // period: selectedRange.toLowerCase()
                    period
                })
            });

            const result = await resp.json();
            if (resp.ok && result.statusDescription.statusCode === 200) {
                setFuelData(result.data || {});
            } else if ([401, 403].includes(result.statusDescription.statusCode)) {
                toast.error(result.statusDescription.description || "Unauthorized access. Redirecting...");
                localStorage.clear();

                setTimeout(() => {
                    window.location.replace('/');
                }, 1500);
            } else {
                console.log(result.statusDescription.description || 'Failed to fetch fuel data');
            }
        } catch (err) {
            toast.error('Error fetching fuel summary: ' + err.message);
        }
    };

    const parsed = Object.entries(fuelData)
        .map(([key, item]) => {
            const isDateKey = /^\d{4}-\d{2}-\d{2}$/.test(key);
            const label = isDateKey
                ? new Date(key).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) // e.g., "05 Jun"
                : monthNames[parseInt(key)];

            return {
                label,
                fuelQty: item.fuelQuantity || 0,
                sortKey: isDateKey ? new Date(key).getTime() : parseInt(key)
            };
        })
        .sort((a, b) => a.sortKey - b.sortKey);

    // const parsed = Object.entries(fuelData)
    //     .map(([key, item]) => {
    //         const isDateKey = /^\d{4}-\d{2}-\d{2}$/.test(key);
    //         let label = '';

    //         if (isDateKey) {
    //             const date = new Date(key);
    //             const month = date.toLocaleString('en-GB', { month: 'short' });
    //             const yearShort = date.getFullYear().toString().slice(-2);
    //             label = `${month}-${yearShort}`;
    //         } else {
    //             const monthIndex = parseInt(key, 10) - 1;
    //             const month = monthNames[monthIndex] || '';
    //             const yearShort = '25';
    //             label = `${month}-${yearShort}`;
    //         }

    //         return {
    //             label,
    //             fuelQty: item.fuelQuantity || 0,
    //             sortKey: isDateKey ? new Date(key).getTime() : parseInt(key),
    //         };
    //     })
    //     .sort((a, b) => a.sortKey - b.sortKey);

    const labels = parsed.map(item => item.label);
    const datasetValues = parsed.map(item => Math.round(item.fuelQty));

    const data = {
        labels,
        datasets: [
            {
                label: 'Fuel Refilling (Liters)',
                data: datasetValues,
                backgroundColor: 'rgba(54, 162, 235, 0.7)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            // title: { display: true, text: 'Fuel Refilling (Liters)' }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Liters' },
                ticks: {
                    callback: function (value) {
                        if (value >= 1_00_00_000) return (value / 1_00_00_000).toFixed(1).replace(/\.0$/, '') + 'Cr';
                        if (value >= 1_00_000) return (value / 1_00_000).toFixed(1).replace(/\.0$/, '') + 'L';
                        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
                        if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
                        return value;
                    }
                }
            },
            x: {
                // title: { display: true, text: 'Month' }
            }
        }
    };

    const filterOptions = [
        { label: 'Year-25', value: 'y-25' },
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'Week', value: 'week' },
        { label: 'Current Month', value: 'cm' },
        { label: 'Prev. Month', value: 'pm' },
        { label: '3M', value: '3m' },
        { label: '6M', value: '6m' },
        { label: '9M', value: '9m' },
    ];

    const handleFilterSelect = (range) => {
        setSelectedRange(range);
        setShowDropdown(false);
    };

    const totals = useMemo(() => {
        return Object.values(fuelData).reduce(
            (acc, item) => {
                acc.fuelQuantity += item?.fuelQuantity || 0;
                return acc;
            },
            { fuelQuantity: 0 }
        );
    }, [fuelData]);

    return (
        
        // <div className="card shadow-sm my-0" style={{ maxWidth: '700px', margin: 'auto' }}>
        //     <div className="card-body">
        //         <div className="d-flex justify-content-between align-items-center mb-4">
        //             <h5 className="card-title mb-0">Fuel Consumption</h5>
        //             <div style={{ position: 'relative' }}>
        //                 <i
        //                     className="fa-solid fa-filter d-none"
        //                     style={{ cursor: 'pointer', fontSize: '18px' }}
        //                     onClick={() => setShowDropdown(!showDropdown)}
        //                 ></i>
        //                 {showDropdown && (
        //                     <div className="dropdown-menu show" style={{ position: 'absolute', right: 0, zIndex: 999 }}>
        //                         {filterOptions.map(({ label, value }) => (
        //                             <button key={value} className="dropdown-item" onClick={() => handleFilterSelect(value)}>
        //                                 {label}
        //                             </button>
        //                         ))}
        //                     </div>
        //                 )}
        //             </div>
        //         </div>
        //         <Bar data={data} options={options} />
        //     </div>

        //     <div className="row mb-3 text-center">
        //         <div className="col-md-4">
        //             <h6 className="mb-1">Total Fuel Consumption</h6>
        //             <p className="fw-bold text-primary mb-0">
        //                 {new Intl.NumberFormat('en-IN').format(Math.round(totals.fuelQuantity))}
        //             </p>
        //         </div>
        //     </div>

        // </div>

        <div className="card shadow-sm my-0" style={{ maxWidth: '700px', margin: 'auto' }}>
            <div className="card-body">

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">Fuel Consumption</h5>
                    <div style={{ position: 'relative' }}>
                        <i
                            className="fa-solid fa-filter d-none"
                            style={{ cursor: 'pointer', fontSize: '18px' }}
                            onClick={() => setShowDropdown(!showDropdown)}
                        ></i>
                        {showDropdown && (
                            <div
                                className="dropdown-menu show"
                                style={{ position: 'absolute', right: 0, zIndex: 999 }}
                            >
                                {filterOptions.map(({ label, value }) => (
                                    <button
                                        key={value}
                                        className="dropdown-item"
                                        onClick={() => handleFilterSelect(value)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="d-flex justify-content-center">
                    <div style={{ width: '100%', maxWidth: '600px' }}>
                        <Bar data={data} options={options} />
                    </div>
                </div>
            </div>

            <div className="row mb-3 text-center">
                <div className="col-md-4 mx-auto">
                    <h6 className="mb-1">Total Fuel Consumption</h6>
                    <p className="fw-bold text-primary mb-0">
                        {new Intl.NumberFormat('en-IN').format(Math.round(totals.fuelQuantity))}
                    </p>
                </div>
            </div>
        </div>

    );
};

export default DashboardVehicleRefilling;